import { useState, useEffect } from "react";
import { collection, onSnapshot, query, orderBy } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../../../firebase.js";
import { useAuth } from "../../../useAuth.js";
import { Link } from "react-router-dom";
import "./userList.css";

const SEASONAL_JACKET_KEYS = [
    { name: 'sleeveLonger', label: 'Sleeve Longer' },
    { name: 'sleeveShorter', label: 'Sleeve Shorter' },
    { name: 'takeInJacket', label: 'Take In' },
    { name: 'takeOutJacket', label: 'Take Out' },
    { name: 'shorterJacket', label: 'Shorter Jacket' },
    { name: 'shoulderPads', label: 'Shoulder Pads' },
    { name: 'functionButtonHole', label: 'Functional Button Hole' },
    { name: 'changeButtonsJacket', label: 'Change Buttons' },
    { name: 'taperSleeve', label: 'Taper Sleeve' },
    { name: 'shatnezTest', label: 'Shattnez Test' },
    { name: 'removeShattnez', label: 'Remove Shattnez' }
];
const SEASONAL_PANTS_KEYS = [
    { name: 'hemming', label: 'Hemming' },
    { name: 'taperPants', label: 'Taper Pants' },
    { name: 'waistIn', label: 'Waist In' },
    { name: 'waistOut', label: 'Waist Out' },
    { name: 'seatIn', label: 'Seat In' },
    { name: 'seatOut', label: 'Seat Out' },
    { name: 'crotchIn', label: 'Crotch In' },
    { name: 'crotchOut', label: 'Crotch Out' },
    { name: 'crotchLining', label: 'Crotch Lining' },
    { name: 'changeZipper', label: 'Change Zipper' },
    { name: 'lengthPants', label: 'Length' },
    { name: 'changeButtonsPants', label: 'Change Buttons' },
    { name: 'suspenderButtons', label: 'Suspender Buttons' }
];
const SEASONAL_SHIRT_KEYS = [
    { name: 'shorterSleeveShirt', label: 'Shorter Sleeve' },
    { name: 'takeInShirt', label: 'Take In' },
    { name: 'takeOutShirt', label: 'Take Out' },
    { name: 'ironShirt', label: 'Iron' }
];
const SEASONAL_VEST_KEYS = [
    { name: 'changeButtonsVest', label: 'Change Buttons' },
    { name: 'takeInVest', label: 'Take In' },
    { name: 'takeOutVest', label: 'Take Out' }
];
const ALL_SEASONAL_KEYS = [...SEASONAL_JACKET_KEYS, ...SEASONAL_PANTS_KEYS, ...SEASONAL_SHIRT_KEYS, ...SEASONAL_VEST_KEYS];

function UserList() {
    const { user: currentUser } = useAuth();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [filterRole, setFilterRole] = useState("all");
    const [editingUser, setEditingUser] = useState(null);
    const [editForm, setEditForm] = useState(null);
    const [saving, setSaving] = useState(false);
    const [editError, setEditError] = useState(null);
    const [userToDelete, setUserToDelete] = useState(null);
    const [deletingUser, setDeletingUser] = useState(false);
    const [deleteError, setDeleteError] = useState(null);
    useEffect(() => {
        // Set up real-time listener for users
        const usersRef = collection(db, "Users");
        const q = query(usersRef, orderBy("name", "asc"));

        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            try {
                const usersList = [];
                
                querySnapshot.forEach((doc) => {
                    const userData = doc.data();
                    console.log('User document:', doc.id, userData); // Debug log
                    
                    // Clean up the data and ensure consistent structure
                    const cleanData = cleanupUserData(userData);
                    usersList.push({
                        uid: doc.id, // Always use document ID as UID
                        ...cleanData
                    });
                });
                
                setUsers(usersList);
                setLoading(false);
                setError(null);
                
            } catch (err) {
                console.error("Error processing users:", err);
                setError("Failed to load users. Please refresh the page.");
                setLoading(false);
            }
        }, (err) => {
            console.error("Error listening to users:", err);
            setError("Failed to connect to users. Please check your connection.");
            setLoading(false);
        });

        // Cleanup function to unsubscribe when component unmounts
        return () => unsubscribe();
    }, []);

    // Filter users based on search term and role filter
    const filteredUsers = users.filter(user => {
        const matchesSearch = user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                             user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                             user.position?.toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesRole = filterRole === "all" || user.role === filterRole;
        
        return matchesSearch && matchesRole;
    });

    const getRoleColor = (role) => {
        switch (role) {
            case 'Admin':
                return 'role-admin';
            case 'User':
                return 'role-user';
            default:
                return 'role-default';
        }
    };

    const getRoleLabel = (role) => {
        switch (role) {
            case 'Admin':
                return 'Administrator';
            case 'User':
                return 'User';
            default:
                return 'Unknown';
        }
    };

    const handleEditUser = (user) => {
        const rates = user.seasonalRates || {};
        const form = {
            name: user.name || '',
            newPassword: '',
            position: user.position || 'Full Time',
            role: user.role || 'User',
            department: user.department || '',
            phone: user.phone || '',
            active: user.active !== false,
            ...Object.fromEntries(ALL_SEASONAL_KEYS.map(({ name }) => [name, rates[name] != null ? String(rates[name]) : '']))
        };
        setEditForm(form);
        setEditingUser(user);
        setEditError(null);
    };

    const handleEditInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setEditForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        if (!editingUser || !editForm) return;
        setSaving(true);
        setEditError(null);
        try {
            const seasonalRates = editForm.position === 'Seasonal'
                ? ALL_SEASONAL_KEYS.reduce((acc, { name }) => {
                    acc[name] = editForm[name] ? parseFloat(editForm[name]) : null;
                    return acc;
                }, {})
                : null;
            const updateUser = httpsCallable(functions, 'updateUser');
            await updateUser({
                uid: editingUser.uid,
                name: editForm.name.trim(),
                newPassword: editForm.newPassword.trim() || undefined,
                position: editForm.position,
                role: editForm.role,
                department: editForm.department.trim() || null,
                phone: editForm.phone.trim() || null,
                active: editForm.active,
                seasonalRates
            });
            setEditingUser(null);
            setEditForm(null);
        } catch (err) {
            setEditError(err.message || 'Failed to update user.');
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteUserClick = (user) => {
        setDeleteError(null);
        setUserToDelete(user);
    };

    const handleDeleteUserConfirm = async () => {
        if (!userToDelete) return;
        setDeletingUser(true);
        setDeleteError(null);
        try {
            const deleteUserFn = httpsCallable(functions, 'deleteUser');
            await deleteUserFn({ uid: userToDelete.uid });
            setUserToDelete(null);
        } catch (err) {
            setDeleteError(err.message || 'Failed to delete user.');
        } finally {
            setDeletingUser(false);
        }
    };

    const handleDeleteUserCancel = () => {
        if (!deletingUser) {
            setUserToDelete(null);
            setDeleteError(null);
        }
    };

    const cleanupUserData = (userData) => {
        // Remove redundant UID field if it exists and matches the document ID
        // This handles existing documents that may have been created with redundant UID fields
        const { UID, uid, ...cleanData } = userData;
        return cleanData;
    };

    if (loading) {
        return (
            <div className="user-list-container">
                <div className="user-list-loading">
                    <div className="loading-spinner"></div>
                    <p>Loading users...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="user-list-container">
                <div className="user-list-error">
                    <div className="error-message">
                        {error}
                    </div>
                    <button 
                        className="retry-btn" 
                        onClick={() => window.location.reload()}
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="user-list-container">
            <div className="user-list-header">
                <h1>User Management</h1>
                <div className="header-actions">
                    {/* <button 
                        className="migrate-btn" 
                        onClick={migrateUserDocuments}
                        title="Clean up existing user documents"
                    >
                        🔧 Migrate Data
                    </button> */}
                    <Link to="/admin/create-user" className="create-user-btn">
                        <span>+</span>
                        Create New User
                    </Link>
                </div>
            </div>

            <div className="user-list-controls">
                <div className="search-section">
                    <input
                        type="text"
                        placeholder="Search users by name, email, or position..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="search-input"
                    />
                </div>

                <div className="filter-section">
                    <label htmlFor="role-filter">Filter by Role:</label>
                    <select
                        id="role-filter"
                        value={filterRole}
                        onChange={(e) => setFilterRole(e.target.value)}
                        className="role-filter-select"
                    >
                        <option value="all">All Roles</option>
                        <option value="Admin">Administrator</option>
                        <option value="User">User</option>
                    </select>
                </div>
            </div>

            <div className="user-list-content">
                {filteredUsers.length === 0 ? (
                    <div className="no-users">
                        <h3>No users found</h3>
                        <p>
                            {searchTerm || filterRole !== "all" 
                                ? "Try adjusting your search or filter criteria."
                                : "There are no users in the system yet."
                            }
                        </p>
                    </div>
                ) : (
                    <div className="users-table-container">
                        <table className="users-table">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Email</th>
                                    <th>Role</th>
                                    <th>Position</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredUsers.map((user) => (
                                    <tr key={user.uid} className="user-row">
                                        <td className="user-name">
                                            <div className="admin-user-avatar">
                                                {user.name?.charAt(0)?.toUpperCase() || 'U'}
                                            </div>
                                            <span style={{color: "black"}}>{user.name || 'Unnamed User'}</span>
                                        </td>
                                        <td className="user-email">{user.email || 'No email'}</td>
                                        <td className="user-role">
                                            <span className={`role-badge ${getRoleColor(user.role)}`}>
                                                {getRoleLabel(user.role)}
                                            </span>
                                        </td>
                                        <td className="user-position">{user.position || 'No position'}</td>
                                        <td className="user-status">
                                            <span className={`status-badge ${user.active ? 'status-active' : 'status-inactive'}`}>
                                                {user.active ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td className="user-actions">
                                            <button className="action-btn edit-btn" title="Edit User" onClick={() => handleEditUser(user)}>
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                                    <path d="m18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                                                </svg>
                                            </button>
                                            <button
                                                className="action-btn delete-btn"
                                                title={currentUser?.uid === user.uid ? 'You cannot delete your own account' : 'Delete User'}
                                                onClick={() => handleDeleteUserClick(user)}
                                                disabled={currentUser?.uid === user.uid}
                                            >
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M3 6h18"/>
                                                    <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/>
                                                    <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/>
                                                    <line x1="10" x2="10" y1="11" y2="17"/>
                                                    <line x1="14" x2="14" y1="11" y2="17"/>
                                                </svg>
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <div className="user-list-footer">
                <div className="user-count">
                    Showing {filteredUsers.length} of {users.length} users
                </div>
            </div>

            {editingUser && editForm && (
                <div className="edit-user-modal-overlay" onClick={() => !saving && (setEditingUser(null), setEditForm(null), setEditError(null))}>
                    <div className="edit-user-modal" onClick={e => e.stopPropagation()}>
                        <div className="edit-user-modal-header">
                            <h2>Edit User</h2>
                            <button type="button" className="edit-user-modal-close" onClick={() => !saving && (setEditingUser(null), setEditForm(null))} aria-label="Close">×</button>
                        </div>
                        <form className="edit-user-form" onSubmit={handleEditSubmit}>
                            <p className="edit-user-email-hint">{editingUser.email}</p>
                            {editError && <div className="edit-user-error">{editError}</div>}
                            <div className="edit-user-grid">
                                <div className="edit-user-field">
                                    <label htmlFor="edit-name">Name</label>
                                    <input id="edit-name" name="name" value={editForm.name} onChange={handleEditInputChange} required />
                                </div>
                                <div className="edit-user-field">
                                    <label htmlFor="edit-newPassword">New password</label>
                                    <input id="edit-newPassword" name="newPassword" type="password" value={editForm.newPassword} onChange={handleEditInputChange} placeholder="Leave blank to keep current" autoComplete="new-password" />
                                </div>
                                <div className="edit-user-field">
                                    <label htmlFor="edit-position">Position</label>
                                    <select id="edit-position" name="position" value={editForm.position} onChange={handleEditInputChange}>
                                        <option value="Full Time">Full Time</option>
                                        <option value="Part Time">Part Time</option>
                                        <option value="Seasonal">Seasonal</option>
                                    </select>
                                </div>
                                <div className="edit-user-field">
                                    <label htmlFor="edit-role">Role</label>
                                    <select id="edit-role" name="role" value={editForm.role} onChange={handleEditInputChange}>
                                        <option value="User">User</option>
                                        <option value="Admin">Administrator</option>
                                    </select>
                                </div>
                                <div className="edit-user-field">
                                    <label htmlFor="edit-department">Department</label>
                                    <input id="edit-department" name="department" value={editForm.department} onChange={handleEditInputChange} />
                                </div>
                                <div className="edit-user-field">
                                    <label htmlFor="edit-phone">Phone</label>
                                    <input id="edit-phone" name="phone" type="tel" value={editForm.phone} onChange={handleEditInputChange} />
                                </div>
                            </div>
                            <div className="edit-user-field edit-user-active">
                                <label>
                                    <input type="checkbox" name="active" checked={editForm.active} onChange={handleEditInputChange} />
                                    Active
                                </label>
                            </div>
                            {editForm.position === 'Seasonal' && (
                                <div className="edit-user-seasonal">
                                    <h4>Seasonal rates</h4>
                                    <div className="edit-user-seasonal-grid">
                                        <div className="edit-user-seasonal-block">
                                            <h5>Jacket</h5>
                                            {SEASONAL_JACKET_KEYS.map(({ name, label }) => (
                                                <div key={name} className="edit-user-rate-field">
                                                    <label htmlFor={`edit-${name}`}>{label}</label>
                                                    <input id={`edit-${name}`} type="number" name={name} value={editForm[name] ?? ''} onChange={handleEditInputChange} min="0" step="1" placeholder="Rate" />
                                                </div>
                                            ))}
                                        </div>
                                        <div className="edit-user-seasonal-block">
                                            <h5>Pants</h5>
                                            {SEASONAL_PANTS_KEYS.map(({ name, label }) => (
                                                <div key={name} className="edit-user-rate-field">
                                                    <label htmlFor={`edit-${name}`}>{label}</label>
                                                    <input id={`edit-${name}`} type="number" name={name} value={editForm[name] ?? ''} onChange={handleEditInputChange} min="0" step="1" placeholder="Rate" />
                                                </div>
                                            ))}
                                        </div>
                                        <div className="edit-user-seasonal-block">
                                            <h5>Shirt</h5>
                                            {SEASONAL_SHIRT_KEYS.map(({ name, label }) => (
                                                <div key={name} className="edit-user-rate-field">
                                                    <label htmlFor={`edit-${name}`}>{label}</label>
                                                    <input id={`edit-${name}`} type="number" name={name} value={editForm[name] ?? ''} onChange={handleEditInputChange} min="0" step="1" placeholder="Rate" />
                                                </div>
                                            ))}
                                        </div>
                                        <div className="edit-user-seasonal-block">
                                            <h5>Vest</h5>
                                            {SEASONAL_VEST_KEYS.map(({ name, label }) => (
                                                <div key={name} className="edit-user-rate-field">
                                                    <label htmlFor={`edit-${name}`}>{label}</label>
                                                    <input id={`edit-${name}`} type="number" name={name} value={editForm[name] ?? ''} onChange={handleEditInputChange} min="0" step="1" placeholder="Rate" />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}
                            <div className="edit-user-actions">
                                <button type="button" className="edit-user-cancel" onClick={() => setEditingUser(null) || setEditForm(null)} disabled={saving}>Cancel</button>
                                <button type="submit" className="edit-user-save" disabled={saving}>{saving ? 'Saving...' : 'Save changes'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {userToDelete && (
                <div className="edit-user-modal-overlay delete-confirm-overlay" onClick={handleDeleteUserCancel}>
                    <div className="edit-user-modal delete-confirm-modal" onClick={e => e.stopPropagation()}>
                        <div className="edit-user-modal-header">
                            <h2>Delete user</h2>
                            <button type="button" className="edit-user-modal-close" onClick={handleDeleteUserCancel} disabled={deletingUser} aria-label="Close">×</button>
                        </div>
                        <div className="delete-confirm-body">
                            <p>Are you sure you want to delete <strong>{userToDelete.name || userToDelete.email}</strong>? This action cannot be undone.</p>
                            {deleteError && <div className="edit-user-error">{deleteError}</div>}
                        </div>
                        <div className="edit-user-actions delete-confirm-actions">
                            <button type="button" className="edit-user-cancel" onClick={handleDeleteUserCancel} disabled={deletingUser}>Cancel</button>
                            <button type="button" className="delete-confirm-btn" onClick={handleDeleteUserConfirm} disabled={deletingUser}>
                                {deletingUser ? 'Deleting...' : 'Yes, delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default UserList;
